  (() => {
    const TAG_NAME = 'xg-premium-mobile-menu';

    if (customElements.get(TAG_NAME)) return;

    customElements.define(TAG_NAME, class extends HTMLElement {
      connectedCallback() {
        if (this._xgConnected) return;
        this._xgConnected = true;

        this.panel = this.querySelector('.xgmm__panel');
        this.overlay = this.querySelector('.xgmm__overlay');
        this.opener = null;
        this.previousOverflow = '';

        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onInsideClick = this.onInsideClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onResize = this.onResize.bind(this);
        this.removeLegacyAgentDrawers = this.removeLegacyAgentDrawers.bind(this);

        /*
          IMPORTANT:
          The old Shopify AI block xg-mobile-drawer-* installs a capture-phase
          document click handler that treats any aria-label containing the word
          "menu" as a hamburger trigger. That is why tapping a dropdown arrow
          could open the old black drawer on templates where that block existed.

          Remove that obsolete custom element from the DOM. Its own
          disconnectedCallback then removes its document click listener.
        */
        this.removeLegacyAgentDrawers(document);

        this.legacyObserver = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((node) => {
              if (node.nodeType === 1) {
                this.removeLegacyAgentDrawers(node);
              }
            });
          });
        });

        this.legacyObserver.observe(document.documentElement, {
          childList: true,
          subtree: true
        });

        document.body.classList.add('xgmm-ready');

        document.addEventListener('click', this.onDocumentClick, true);
        this.addEventListener('click', this.onInsideClick);
        document.addEventListener('keydown', this.onKeydown);
        window.addEventListener('resize', this.onResize, { passive: true });

        this.querySelectorAll('.xgmm__row').forEach((row) => {
          const toggle = row.querySelector(':scope > .xgmm__main > [data-xg-toggle]');
          const panel = row.querySelector(':scope > .xgmm__subpanel');

          if (toggle && panel && toggle.getAttribute('aria-expanded') === 'true') {
            row.classList.add('is-expanded');
            panel.classList.add('is-open');
            panel.setAttribute('aria-hidden', 'false');
          }
        });

        this.querySelectorAll('.xgmm__subrow').forEach((row) => {
          const toggle = row.querySelector(':scope > .xgmm__submain > [data-xg-toggle]');
          const panel = row.querySelector(':scope > .xgmm__subpanel');

          if (toggle && panel && toggle.getAttribute('aria-expanded') === 'true') {
            row.classList.add('is-expanded');
            panel.classList.add('is-open');
            panel.setAttribute('aria-hidden', 'false');
          }
        });
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick, true);
        this.removeEventListener('click', this.onInsideClick);
        document.removeEventListener('keydown', this.onKeydown);
        window.removeEventListener('resize', this.onResize);

        if (this.legacyObserver) {
          this.legacyObserver.disconnect();
          this.legacyObserver = null;
        }

        if (!document.querySelector(TAG_NAME)) {
          document.body.classList.remove('xgmm-ready', 'xgmm-open');
        }
      }

      removeLegacyAgentDrawers(root) {
        const removeElement = (element) => {
          if (!(element instanceof Element)) return;
          if (element === this || element.closest(TAG_NAME)) return;

          const tagName = element.tagName.toLowerCase();
          const isOldCustomDrawer = tagName.startsWith('xg-mobile-drawer-');
          const hasOldDrawerClass = [...element.classList].some((className) =>
            className.startsWith('ai-xg-drawer-')
          );

          if (isOldCustomDrawer || hasOldDrawerClass) {
            element.remove();
          }
        };

        if (root instanceof Element) {
          removeElement(root);
          if (!root.isConnected && root !== document.documentElement) return;
        }

        const scope = root && root.querySelectorAll ? root : document;

        scope.querySelectorAll('[class*="ai-xg-drawer-"]').forEach(removeElement);

        scope.querySelectorAll('*').forEach((element) => {
          if (element.tagName.toLowerCase().startsWith('xg-mobile-drawer-')) {
            removeElement(element);
          }
        });
      }

      isMobile() {
        return window.matchMedia('(max-width: 1024px)').matches;
      }

      isHamburgerTrigger(target) {
        if (!(target instanceof Element)) return null;

        const trigger = target.closest(
          '[data-mobile-menu], .header-mobile--wrapper .mobileMenu-toggle, .mobile-header-1 .mobileMenu-toggle, .header-mobile .mobileMenu-toggle'
        );

        if (!trigger) return null;
        if (trigger.closest(TAG_NAME)) return null;

        return trigger;
      }

      onDocumentClick(event) {
        if (!this.isMobile()) return;

        const trigger = this.isHamburgerTrigger(event.target);
        if (!trigger) return;

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        this.open(trigger);
      }

      onInsideClick(event) {
        const closeButton = event.target.closest('[data-xg-close]');

        if (closeButton && this.contains(closeButton)) {
          event.preventDefault();
          event.stopPropagation();
          this.close();
          return;
        }

        const toggle = event.target.closest('[data-xg-toggle]');

        if (toggle && this.contains(toggle)) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          this.toggleSubmenu(toggle);
          return;
        }

        /*
          If a main/second-level item has children, tapping its text/icon opens
          the accordion too. This prevents accidental navigation when the user
          expects MEN'S CLOTHING / Sweatpants to expand.
        */
        const mainLink = event.target.closest('.xgmm__main-link');
        if (mainLink && this.contains(mainLink)) {
          const row = mainLink.closest('.xgmm__row');
          const rowToggle = row?.querySelector(':scope > .xgmm__main > [data-xg-toggle]');

          if (rowToggle) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            this.toggleSubmenu(rowToggle);
            return;
          }
        }

        const subLink = event.target.closest('.xgmm__sublink');
        if (subLink && this.contains(subLink)) {
          const subrow = subLink.closest('.xgmm__subrow');
          const subToggle = subrow?.querySelector(':scope > .xgmm__submain > [data-xg-toggle]');

          if (subToggle) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            this.toggleSubmenu(subToggle);
            return;
          }
        }

        const link = event.target.closest(
          '.xgmm__main-link, .xgmm__sublink, .xgmm__grandlink, .xgmm__utility'
        );

        if (link && this.contains(link)) {
          event.stopPropagation();
        }
      }

      toggleSubmenu(toggle) {
        const panelId = toggle.getAttribute('aria-controls');
        if (!panelId) return;

        const panel = this.querySelector('#' + CSS.escape(panelId));
        if (!panel) return;

        const expanded = toggle.getAttribute('aria-expanded') === 'true';
        const willOpen = !expanded;

        toggle.setAttribute('aria-expanded', String(willOpen));
        panel.setAttribute('aria-hidden', String(!willOpen));
        panel.classList.toggle('is-open', willOpen);

        const parentRow = toggle.closest('.xgmm__row, .xgmm__subrow');
        if (parentRow) {
          parentRow.classList.toggle('is-expanded', willOpen);
        }
      }

      open(trigger) {
        if (!this.isMobile() || !this.panel) return;

        this.removeLegacyAgentDrawers(document);

        this.opener = trigger || null;

        this.classList.add('is-open');
        this.style.pointerEvents = 'auto';
        this.setAttribute('aria-hidden', 'false');

        this.panel.style.pointerEvents = 'auto';
        this.panel.setAttribute('aria-hidden', 'false');

        if (this.overlay) {
          this.overlay.style.pointerEvents = 'auto';
        }

        if (this.opener) {
          this.opener.setAttribute('aria-expanded', 'true');
        }

        this.previousOverflow = document.documentElement.style.overflow;
        document.documentElement.style.overflow = 'hidden';
        document.body.classList.add('xgmm-open');

        const legacyDrawer = document.querySelector('#navigation-mobile');
        if (legacyDrawer) {
          legacyDrawer.setAttribute('aria-hidden', 'true');
          legacyDrawer.style.pointerEvents = 'none';
        }

        requestAnimationFrame(() => {
          const closeButton = this.querySelector('.xgmm__close');

          if (closeButton) {
            closeButton.focus({ preventScroll: true });
          } else {
            this.panel.focus({ preventScroll: true });
          }
        });
      }

      close() {
        if (!this.classList.contains('is-open')) return;

        this.classList.remove('is-open');
        this.style.pointerEvents = 'none';
        this.setAttribute('aria-hidden', 'true');

        if (this.panel) {
          this.panel.style.pointerEvents = '';
          this.panel.setAttribute('aria-hidden', 'true');
        }

        if (this.overlay) {
          this.overlay.style.pointerEvents = '';
        }

        document.body.classList.remove('xgmm-open');
        document.documentElement.style.overflow = this.previousOverflow || '';

        const legacyDrawer = document.querySelector('#navigation-mobile');
        if (legacyDrawer) {
          legacyDrawer.style.pointerEvents = '';
        }

        if (this.opener && this.opener.isConnected) {
          this.opener.setAttribute('aria-expanded', 'false');
          this.opener.focus({ preventScroll: true });
        }

        this.opener = null;
      }

      onResize() {
        if (!this.isMobile() && this.classList.contains('is-open')) {
          this.close();
        }
      }

      getFocusableElements() {
        if (!this.panel) return [];

        return [...this.panel.querySelectorAll(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )].filter((element) => {
          return element.offsetParent !== null && getComputedStyle(element).visibility !== 'hidden';
        });
      }

      onKeydown(event) {
        if (!this.classList.contains('is-open')) return;

        if (event.key === 'Escape') {
          event.preventDefault();
          this.close();
          return;
        }

        if (event.key !== 'Tab') return;

        const focusable = this.getFocusableElements();
        if (!focusable.length) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    });
  })();
